import {OWN_WIDGET_ROLES} from '../focus/keys'

/**
 * Whether this event belongs to something other than the card.
 *
 * Two ways it can, and they are different questions.
 *
 * **It was drawn somewhere else.** A caller draws whatever it likes in a card, including
 * an overflow menu or a dialog, and those render through a portal: the element is
 * appended to a container elsewhere in the document, while React still routes the event
 * up its own tree to the card that rendered it. So the card hears an event on an element
 * that is not inside it. Everything in such a subtree belongs to whatever opened it,
 * interactive or not -- the body text of a dialog is not a gesture on the card behind it.
 *
 * **It was drawn here, on a control of its own.** A card's title is usually a link, and a
 * link is a tab stop in its own right, which is why a card already takes focus reported
 * from anywhere inside it. Taking its click, its `Space` or its press as a gesture on the
 * card would act on the card every time somebody used the control.
 *
 * Matched by what the element is rather than by whether it is focusable, because the card
 * itself carries a `tabIndex` and so does everything the board makes a roving item. An
 * interactive element the card sits *inside* is not one of its own, which is what the
 * second containment check is for.
 *
 * Asked by both of a card's pointer behaviours -- the selection gesture and the drag --
 * because a press that is not the card's is not the card's for either of them.
 */
export function isSomebodyElsesEvent(target: EventTarget | null, cardElement: HTMLElement): boolean {
  if (!(target instanceof Element)) return false

  if (!cardElement.contains(target)) return true

  const control = target.closest(INTERACTIVE)

  return control !== null && control !== cardElement && cardElement.contains(control)
}

const INTERACTIVE = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  'label',
  // A media element with `controls` draws a row of buttons the board never sees, so a
  // click on one arrives as a click on the element itself.
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  // The controls of a card's own, plus every composite widget the arrow-key guard names:
  // a slider or a combobox a caller drew is no more the card's click than a button is.
  ...['button', 'link', 'checkbox', 'switch', 'menuitem', 'tab', ...OWN_WIDGET_ROLES].map(role => `[role="${role}"]`),
].join(', ')
